import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Container, Paper } from '@mui/material';
import { IPostContent } from 'models/myfeed';
import { axiosPostMyFeedPostId } from 'services/api';
import { extractIdFromSlug } from 'utilities/helpers';

export interface MarkdownMirrorProps {
  title?: string;
  markdownContent?: string;
  author?: string;
  date?: string;
  allowBlogPath?: boolean;
  children: React.ReactNode;
}

const pageTitles: Record<string, string> = {
  '/': '4um Home',
  '/home': '4um Home',
  '/signin': 'Sign in to 4um',
  '/privacy': 'Privacy Policy',
  '/aboutus': 'About 4um',
  '/groups': 'Groups on 4um',
  '/chat': '4um Chat',
  '/createPost': 'Create a Post on 4um',
  '/createPoll': 'Create a Poll on 4um',
  '/notification': 'Notifications',
  '/search': 'Search 4um',
  '/consent': '4um Consent'
};

const getPageTitle = (pathname: string) => {
  const exactTitle = pageTitles[pathname];
  if (exactTitle) return exactTitle;
  if (pathname.startsWith('/profile/')) return '4um Profile';
  if (pathname.startsWith('/group/')) return '4um Group';
  if (pathname.startsWith('/post/')) return '4um Post';
  if (pathname.startsWith('/poll/')) return '4um Poll';
  if (pathname.startsWith('/friendlist/')) return 'Friends on 4um';
  if (pathname.startsWith('/grouplist/')) return 'Group Members on 4um';
  if (pathname.startsWith('/about-us/blog/')) return '4um Insight';
  return '4um';
};

const htmlToMarkdown = (value?: string): string => {
  if (!value) return '';

  const decoder = document.createElement('textarea');
  decoder.innerHTML = value.replace(/\\"/g, '"').replace(/\\n/g, '\n');
  const decodedValue = decoder.value;

  if (!/<[a-z][\s\S]*>/i.test(decodedValue)) {
    return decodedValue.trim();
  }

  const documentFragment = new DOMParser().parseFromString(decodedValue, 'text/html');
  const renderNode = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent || '';
    if (node.nodeType !== Node.ELEMENT_NODE) return '';

    const element = node as HTMLElement;
    const content = Array.from(element.childNodes).map(renderNode).join('');
    const tagName = element.tagName.toLowerCase();

    if (tagName === 'a') {
      const href = element.getAttribute('href');
      return href ? `[${content.trim() || href}](${href})` : content;
    }
    if (tagName === 'br') return '\n';
    if (tagName === 'strong' || tagName === 'b') return `**${content.trim()}**`;
    if (tagName === 'em' || tagName === 'i') return `*${content.trim()}*`;
    if (/^h[1-6]$/.test(tagName)) {
      const level = Number(tagName.substring(1));
      return `${'#'.repeat(level)} ${content.trim()}\n\n`;
    }
    if (tagName === 'li') return `- ${content.trim()}\n`;
    if (['p', 'div', 'section', 'article', 'blockquote', 'ul', 'ol'].includes(tagName)) {
      return `${content.trim()}\n\n`;
    }

    return content;
  };

  return renderNode(documentFragment.body)
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const getPostMarkdown = (post: IPostContent, isPoll: boolean): string => {
  const content = htmlToMarkdown(isPoll
    ? post.description || post.thought || post.question
    : post.description || post.thought) || 'Join the discussion on 4um.';
  const author = post.user?.username || post.user?.summary?.userName || '4um Member';
  const media = (post.mediaDtos || [])
    .filter((item) => item.directUrl)
    .map((item) => `![${item.fileName || 'Post media'}](${item.directUrl})`)
    .join('\n\n');
  const pollData = post as IPostContent & {
    pollChoices?: { choice?: string; text?: string; option?: string; numVotes?: number }[];
    options?: { choice?: string; text?: string; option?: string; numVotes?: number }[];
    pollOptions?: { choice?: string; text?: string; option?: string; numVotes?: number }[];
  };
  const rawPollChoices = (post.pollDetails?.pollChoices || pollData.pollChoices || pollData.options || pollData.pollOptions) as {
    choice?: string;
    text?: string;
    option?: string;
    numVotes?: number;
  }[] | undefined;
  const pollChoices = rawPollChoices
    ?.map((choice) => {
      const value = choice.choice || choice.text || choice.option || '';
      return `${value}${choice.numVotes !== undefined ? ` (${choice.numVotes} votes)` : ''}`;
    })
    .filter(Boolean)
    .join('\n');

  return [
    `## ${isPoll ? 'Poll question' : 'Post content'}`,
    content,
    `**Author:** ${author}`,
    post.group?.name ? `**Community:** ${post.group.name}` : '',
    isPoll && pollChoices ? `### Poll choices\n\n${pollChoices}` : '',
    media ? `### Shared media\n\n${media}` : '',
    post.numComments !== undefined ? `**Comments:** ${post.numComments}` : ''
  ].filter(Boolean).join('\n\n');
};

export const MarkdownMirror: React.FC<MarkdownMirrorProps> = ({
  title,
  markdownContent,
  author,
  date,
  allowBlogPath = false,
  children
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location.search);
  const isMarkdownMode = searchParams.get('format') === 'md' || searchParams.get('markdown') === 'true';
  const isPostPage = location.pathname.startsWith('/post/') || location.pathname.startsWith('/poll/');
  const isPollPage = location.pathname.startsWith('/poll/');
  const [dynamicContent, setDynamicContent] = useState<string>();
  const [dynamicTitle, setDynamicTitle] = useState<string>();
  const [dynamicAuthor, setDynamicAuthor] = useState<string>();
  const [dynamicDate, setDynamicDate] = useState<string>();

  useEffect(() => {
    let active = true;

    if (!isMarkdownMode || !isPostPage) {
      setDynamicContent(undefined);
      setDynamicTitle(undefined);
      setDynamicAuthor(undefined);
      setDynamicDate(undefined);
      return () => {
        active = false;
      };
    }

    const routeId = location.pathname
      .split('/')
      .filter(Boolean)
      .pop();
    const postId = extractIdFromSlug(routeId);

    if (!postId) {
      setDynamicContent('The requested 4um content could not be identified.');
      return () => {
        active = false;
      };
    }

    axiosPostMyFeedPostId({ postId })
      .then((post: IPostContent) => {
        if (!active) return;
        const isPoll = isPollPage || post.postType === 'POLL';
        const content = htmlToMarkdown(isPoll
          ? post.question || post.title || post.description || post.thought
          : post.title || post.description || post.thought);
        setDynamicContent(getPostMarkdown(post, isPoll));
        setDynamicTitle(content.slice(0, 80) || (isPoll ? '4um Poll' : '4um Post'));
        setDynamicAuthor(post.user?.username || post.user?.summary?.userName);
        setDynamicDate(post.createdAt);
      })
      .catch(() => {
        if (active) setDynamicContent('The requested 4um content is unavailable.');
      });

    return () => {
      active = false;
    };
  }, [isMarkdownMode, isPollPage, isPostPage, location.pathname]);

  if (!isMarkdownMode || (location.pathname.startsWith('/about-us/blog/') && !allowBlogPath)) {
    return <>{children}</>;
  }

  const pageTitle = dynamicTitle || title || getPageTitle(location.pathname);
  const pageContent = dynamicContent || markdownContent || `Loading the 4um content at ${location.pathname}...`;
  const rawMarkdownText = `# ${pageTitle}

${dynamicAuthor || author ? `**Author:** ${dynamicAuthor || author}\n` : ''}${dynamicDate || date ? `**Date:** ${dynamicDate || date}\n` : ''}
---

${pageContent}`;

  const handleSwitchToHtml = () => {
    searchParams.delete('format');
    searchParams.delete('markdown');
    const newSearch = searchParams.toString();
    navigate({
      pathname: location.pathname,
      search: newSearch ? `?${newSearch}` : ''
    });
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 4,
          backgroundColor: '#0d1117',
          color: '#c9d1d9',
          fontFamily: 'Consolas, Monaco, "Andale Mono", "Ubuntu Mono", monospace',
          borderRadius: 2,
          border: '1px solid #30363d'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="caption" sx={{ color: '#8b949e', textTransform: 'uppercase', letterSpacing: 1 }}>
            Markdown Mirror View (`.md`)
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={handleSwitchToHtml}
            sx={{
              color: '#58a6ff',
              borderColor: '#30363d',
              '&:hover': { borderColor: '#58a6ff', backgroundColor: 'rgba(56, 139, 253, 0.1)' }
            }}
          >
            Switch to Standard UI
          </Button>
        </Box>

        <Box component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.95rem', lineHeight: 1.6, m: 0 }}>
          {rawMarkdownText}
        </Box>
      </Paper>
    </Container>
  );
};

export default MarkdownMirror;